import { observer } from 'mobx-react'
import React, { Suspense } from 'react'
import { useIntl } from 'react-intl'
import { Navigate, Route, Routes } from 'react-router-dom'
import {
  isAdmin,
  isAdminLabelAA,
  isDataCustomizer,
  isDealer,
  isDealerOperator,
  isLooker,
} from '../services/AccountService'
import NotFound from '../views/NotFound/NotFound'
import RequestsManagement from '../views/RequestsManagement/RequestsManagement'
import Loader from './Loader/Loader'

const SummaryStatisticsDealers = React.lazy(
  () => import('../views/SummaryStatisticsDealers/SummaryStatisticsDealers'),
)
const StatisticsInstallationsSubsidies = React.lazy(
  () =>
    import('../views/StatisticsInstallationsSubsidies/StatisticsInstallationsSubsidies'),
)
const Editor = React.lazy(() => import('../views/Management/Editor'))
const Agreements = React.lazy(() => import('../views/Agreements/Agreements'))
const AdditionalSummary = React.lazy(
  () => import('../views/AdditionalSummary/AdditionalSummary'),
)
const DataSettings = React.lazy(
  () => import('../views/DataSettings/DataSettings'),
)
const InstallationSummary = React.lazy(
  () => import('../views/InstallationSummary/InstallationSummary'),
)
const NewReports = React.lazy(() => import('../views/NewReports/NewReports'))
const OperationalSummary = React.lazy(
  () => import('../views/OperationalSummary/OperationalSummary'),
)
const ReportsSummery = React.lazy(
  () => import('../views/ReportsSummery/ReportsSummery'),
)
const Statistics = React.lazy(() => import('../views/Statistics/Statistics'))
const Service = React.lazy(() => import('../views/Service/Service'))
const AddToRequestReport = React.lazy(
  () => import('../views/AddToRequestReport/AddToRequestReport'),
)
const AddToServiceReports = React.lazy(
  () => import('../views/AddToServiceReports/AddToServiceReports'),
)
const ApplicationServiceStatistics = React.lazy(
  () =>
    import('../views/ApplicationServiceStatistics/ApplicationServiceStatistics'),
)
const CheckEquipmentSettings = React.lazy(
  () => import('../views/CheckEquipmentSettings/CheckEquipmentSettings'),
)
const CreateReport = React.lazy(
  () => import('../views/CreateRequestReport/CreateReport'),
)
const CreateServiceReports = React.lazy(
  () => import('../views/CreateServiceReports/CreateServiceReports'),
)
const Customers = React.lazy(() => import('../views/Customers/Customers'))
const DealersSettings = React.lazy(
  () => import('../views/DealersSettings/DealersSettings'),
)
const MergeCities = React.lazy(() => import('../views/MergeCities/MergeCities'))
const RequestsArchive = React.lazy(
  () => import('../views/RequestsArchive/RequestsArchive'),
)
const RequestsStatistic = React.lazy(
  () => import('../views/RequestsStatistic/RequestsStatistic'),
)
const ServiceReports = React.lazy(
  () => import('../views/ServiceReports/ServiceReports'),
)
const SingleCustomer = React.lazy(
  () => import('../views/SingleCustomer/SingleCustomer'),
)
const SingleRequestPage = React.lazy(
  () => import('../views/SingleRequestPage/SingleRequestPage'),
)
const SingleRequestReport = React.lazy(
  () => import('../views/SingleRequestReport/SingleRequestReport'),
)
const SingleServiceReport = React.lazy(
  () => import('../views/SingleServiceReport/SingleServiceReport'),
)
const SummaryStatisticsService = React.lazy(
  () => import('../views/SummaryStatisticsService/SummaryStatisticsService'),
)
const SingleServiceRequest = React.lazy(
  () => import('../views/SingleServiceRequest/SingleServiceRequest'),
)
const Equipment = React.lazy(() => import('../views/Equipment/Equipment'))
const SimilarAddress = React.lazy(
  () => import('../views/SimilarAddress/SimilarAddress'),
)

export const getRoutesData = (intl) => [
  {
    component: <DataSettings />,
    path: '/data-settings',
    visible: isDataCustomizer,
    pageTitle: intl.formatMessage({ id: 'Ввод оперативных данных' }),
  },
  {
    component: <AdditionalSummary />,
    path: '/additional-summary',
    visible: isAdmin,
    pageTitle: intl.formatMessage({ id: 'Инфографика' }),
  },
  {
    component: <CreateServiceReports />,
    path: '/service-reports/create',
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({
      id: 'Отчеты по заявкам на обслуживание',
    }),
  },
  {
    path: '/service-reports/:reportId',
    component: <SingleServiceReport />,
    visible: () => isAdmin() || isDealer() || isDealerOperator(),
    pageTitle: intl.formatMessage({ id: 'Отчеты по заявкам на обслуживание' }),
  },
  {
    path: '/service-reports/:reportId/add',
    component: <AddToServiceReports />,
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({ id: 'Отчеты по заявкам на обслуживание' }),
  },
  {
    component: <ServiceReports />,
    path: '/service-reports',
    visible: () => isAdmin() || isDealer() || isDealerOperator(),
    pageTitle: intl.formatMessage({
      id: 'Отчеты по заявкам на обслуживание',
    }),
  },
  {
    component: <Service />,
    path: '/service',
    visible: () => true,
    pageTitle: intl.formatMessage({ id: 'Заявки на обслуживание' }),
  },
  {
    path: '/service/:serviceRequestId',
    component: <SingleServiceRequest />,
    visible: () => true,
    pageTitle: intl.formatMessage({
      id: 'Заявки на обслуживание',
    }),
  },
  {
    component: <RequestsManagement />,
    path: '/requests',
    visible: () => true,
    pageTitle: intl.formatMessage({ id: 'Заявки на установку' }),
  },
  {
    component: <SingleRequestPage />,
    path: '/requests/:requestId',
    visible: () => isAdmin(),
    pageTitle: intl.formatMessage({ id: 'Заявки на установку' }),
  },

  {
    component: <RequestsArchive />,
    path: '/requests-archive',
    visible: () => isAdmin(),
    pageTitle: intl.formatMessage({ id: 'Архив заявок на установку' }),
  },

  {
    component: <CheckEquipmentSettings />,
    path: '/check-equipment-settings',
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({ id: 'Проверка настроек оборудования' }),
  },
  {
    component: <CreateReport />,
    path: '/reports/create',
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({ id: 'Отчеты по заявкам на установку' }),
  },
  {
    path: '/reports/:reportId',
    component: <SingleRequestReport />,
    visible: () => isAdmin() || isDealer() || isDealerOperator(),
    pageTitle: isAdmin()
      ? intl.formatMessage({
          id: 'Проверка отчётов',
        })
      : intl.formatMessage({ id: 'Отчеты по заявкам на установку' }),
  },
  {
    path: '/reports/:reportId/add',
    component: <AddToRequestReport />,
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({ id: 'Отчеты по заявкам на установку' }),
  },
  {
    component: <NewReports />,
    path: '/reports',
    visible: () => isAdmin() || isDealer() || isDealerOperator(),
    pageTitle: isAdmin()
      ? intl.formatMessage({
          id: 'Проверка отчётов',
        })
      : intl.formatMessage({ id: 'Отчеты по заявкам на установку' }),
  },
  {
    component: <SimilarAddress />,
    path: '/similar-address',
    visible: () => !isLooker() && isAdmin(),
    pageTitle: intl.formatMessage({ id: 'Управление дубликатами' }),
  },
  {
    component: <DealersSettings />,
    path: '/dealers-settings',
    visible: () => !isLooker() && isAdmin(),
    pageTitle: intl.formatMessage({ id: 'Управление Дилерами' }),
  },
  {
    component: <Customers />,
    path: '/customers',
    visible: () => !isLooker() && isAdmin(),
    pageTitle: intl.formatMessage({ id: 'Управление Абонентами' }),
  },
  {
    path: '/customers/:customerId',
    component: <SingleCustomer />,
    visible: () => true,
    pageTitle: intl.formatMessage({
      id: 'Управление Абонентами',
    }),
  },
  {
    component: <Equipment />,
    path: '/equipments',
    visible: () => (!isLooker() && isAdmin()) || isDealer(),
    pageTitle: intl.formatMessage({ id: 'Управление оборудованием' }),
  },
  {
    component: <MergeCities />,
    path: '/merge-cities',
    visible: () => !isLooker() && isAdminLabelAA(),
    pageTitle: intl.formatMessage({
      id: 'Редактор справочников населённых пунктов',
    }),
  },
  {
    component: <Statistics />,
    path: '/statistics',
    visible: isAdmin,
    pageTitle: intl.formatMessage({ id: 'Статистика заявок по добавлению' }),
  },
  {
    component: <OperationalSummary />,
    path: '/operational-summary',
    visible: () => isAdminLabelAA(),
    pageTitle: intl.formatMessage({ id: 'Статистика статусов заявок' }),
  },
  {
    component: <InstallationSummary />,
    path: '/installation-summary',
    visible: isAdmin,
    pageTitle: intl.formatMessage({ id: 'Статистика установок по регионам' }),
  },
  {
    component: <ReportsSummery />,
    path: '/reports-summary',
    visible: isAdmin,
    pageTitle: intl.formatMessage({ id: 'Статистика по отчётам дилеров' }),
  },
  {
    component: <RequestsStatistic />,
    path: '/statistic-by-created-request',
    visible: () => isAdmin() && !isLooker(),
    pageTitle: intl.formatMessage({ id: 'Статистика по созданным заявкам' }),
  },
  {
    component: <Agreements />,
    path: '/agreements',
    visible: () => isDealer(),
    pageTitle: intl.formatMessage({ id: 'Подтверждение договоров' }),
  },
  {
    path: '/',
    component: <Navigate to={'/requests'} />,
    visible: () => true,
  },
  {
    component: <StatisticsInstallationsSubsidies />,
    path: '/finances-summary',
    visible: () => isDataCustomizer(),
    pageTitle: intl.formatMessage({ id: 'Статистика установок по дотациям' }),
  },
  {
    component: <SummaryStatisticsDealers />,
    path: '/summary-statistics-dealers',
    visible: () => isDataCustomizer(),
    pageTitle: intl.formatMessage({
      id: 'Сводная статистика установок по дилерам',
    }),
  },
  {
    component: <SummaryStatisticsService />,
    path: '/summary-statistics-service',
    visible: () => isDataCustomizer(),
    pageTitle: intl.formatMessage({
      id: 'Сводная статистика ремонтов по регионам',
    }),
  },
  {
    component: <ApplicationServiceStatistics />,
    path: '/application-service-statistics',
    visible: () => isDataCustomizer(),
    pageTitle: intl.formatMessage({ id: 'Статистика обслуживания заявок' }),
  },
  {
    component: <Editor />,
    path: '/editor',
    visible: () => isAdminLabelAA(),
    pageTitle: intl.formatMessage({
      id: 'Редактор дотационных населённых пунктов',
    }),
  },
  {
    path: '*',
    component: <NotFound />,
    visible: () => true,
  },
]

const PrivateContent = () => {
  const intl = useIntl()
  const routesData = getRoutesData(intl)

  const setContent = ({ component, path, visible }) => {
    return visible() ? (
      <Route
        exact
        path={path}
        element={
          <Suspense fallback={<Loader fullPage />}>{component}</Suspense>
        }
      />
    ) : (
      <></>
    )
  }
  return <Routes>{routesData.map((data) => setContent(data))}</Routes>
}

export default observer(PrivateContent)
